import { useState, useRef } from "react";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import "./AddInventoryManually.css";

const AddInventoryManually = () => {
  const [manualDrug, setManualDrug] = useState({
    generic_name: "",
    brand_name: "",
    category: "",
    quantity: "",
    price: "",
    cost_price: "",
    wholesale_price: "",
    expiration_date: "",
    nafdac_number: "",
    strength: "",
    dosage_form: "",
    pack_size: "",
    unit: "",
    manufacturer: "",
    description: "",
    batch_number: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const fieldRefs = {
    generic_name: useRef(null),
    quantity: useRef(null),
    price: useRef(null),
    cost_price: useRef(null),
    wholesale_price: useRef(null),
  };

  const bottomRef = useRef(null);

  const normalizeText = (text, mode = "lower") => {
    if (!text) return "";
    const trimmed = text.trim();
    if (mode === "upper") return trimmed.toUpperCase();
    if (mode === "none") return trimmed;
    return trimmed.toLowerCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");
    setLoading(true);

    const newErrors = {};
    if (!manualDrug.generic_name.trim() && !manualDrug.brand_name.trim())
      newErrors.generic_name = "Provide at least a Generic or Brand Name.";
    if (!manualDrug.quantity || parseInt(manualDrug.quantity) <= 0)
      newErrors.quantity = "Quantity must be positive.";
    if (!manualDrug.price || parseFloat(manualDrug.price) <= 0)
      newErrors.price = "Selling price must be valid.";
    if (
      manualDrug.cost_price !== "" &&
      (isNaN(parseFloat(manualDrug.cost_price)) ||
        parseFloat(manualDrug.cost_price) < 0)
    )
      newErrors.cost_price = "Cost price must be a valid positive number.";
    if (
      manualDrug.wholesale_price !== "" &&
      (isNaN(parseFloat(manualDrug.wholesale_price)) ||
        parseFloat(manualDrug.wholesale_price) < 0)
    )
      newErrors.wholesale_price = "Wholesale price must be a valid positive number.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstField = Object.keys(newErrors)[0];
      if (fieldRefs[firstField]?.current) {
        fieldRefs[firstField].current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        fieldRefs[firstField].current.focus();
      }
      setLoading(false);
      return;
    }

    const payload = {
      ...manualDrug,
      generic_name: normalizeText(manualDrug.generic_name, "lower"),
      brand_name: normalizeText(manualDrug.brand_name, "lower"),
      category: manualDrug.category.trim() || "N/A",
      nafdac_number: normalizeText(manualDrug.nafdac_number, "upper") || "N/A",
      strength: normalizeText(manualDrug.strength, "none") || "N/A",
      dosage_form: normalizeText(manualDrug.dosage_form, "lower") || "N/A",
      pack_size: manualDrug.pack_size.trim() || "N/A",
      unit: manualDrug.unit.trim() || "N/A",
      manufacturer: manualDrug.manufacturer.trim() || "N/A",
      description: manualDrug.description.trim() || "N/A",
      batch_number: manualDrug.batch_number.trim() || "N/A",
      expiration_date: manualDrug.expiration_date || null,
      cost_price:
        manualDrug.cost_price !== ""
          ? parseFloat(manualDrug.cost_price)
          : null,
      wholesale_price:
        manualDrug.wholesale_price !== ""
          ? parseFloat(manualDrug.wholesale_price)
          : null,
    };

    try {
      await api.post("/pharmacies/add", payload);

      setSuccess("✅ Item added successfully!");
      setManualDrug({
        generic_name: "",
        brand_name: "",
        category: "",
        quantity: "",
        price: "",
        cost_price: "",
        wholesale_price: "",
        expiration_date: "",
        nafdac_number: "",
        strength: "",
        dosage_form: "",
        pack_size: "",
        unit: "",
        manufacturer: "",
        description: "",
        batch_number: "",
      });
    } catch (err) {
      const message =
        err.response?.data?.detail || err.message || "❌ Failed to add item.";
      setErrors({ form: message });
    } finally {
      setLoading(false);
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  return (
    <div className="addinv-dashboard-container">
      <Sidebar />
      <div className="addinv-main-dashboard">
        <h2>➕ Add Inventory Manually</h2>

        <form className="addinv-form" onSubmit={handleSubmit}>
          {/* ── Name fields ── */}
          <label className="addinv-name-label">
            Provide at least one name{" "}
            <small>(Generic name or Brand name is required)</small>
          </label>

          <input
            ref={fieldRefs.generic_name}
            type="text"
            placeholder="Generic Name"
            value={manualDrug.generic_name}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, generic_name: e.target.value })
            }
          />
          {errors.generic_name && (
            <p className="addinv-error-message">{errors.generic_name}</p>
          )}

          <input
            type="text"
            placeholder="Brand Name"
            value={manualDrug.brand_name}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, brand_name: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Category (optional)"
            value={manualDrug.category}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, category: e.target.value })
            }
          />

          {/* ── Quantity ── */}
          <input
            ref={fieldRefs.quantity}
            type="number"
            placeholder="Quantity"
            className={errors.quantity ? "addinv-input-error" : ""}
            value={manualDrug.quantity}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, quantity: e.target.value })
            }
          />
          {errors.quantity && (
            <p className="addinv-error-message">{errors.quantity}</p>
          )}

          {/* ── Selling Price ── */}
          <label className="addinv-name-label">
            Selling Price <small>(price charged to retail customers)</small>
          </label>
          <div className="addinv-price-input-wrapper" ref={fieldRefs.price}>
            <span>₦</span>
            <input
              type="number"
              placeholder="Selling Price"
              className={errors.price ? "addinv-input-error" : ""}
              value={manualDrug.price}
              min="0"
              step="0.01"
              onChange={(e) =>
                setManualDrug({ ...manualDrug, price: e.target.value })
              }
            />
          </div>
          {errors.price && (
            <p className="addinv-error-message">{errors.price}</p>
          )}

          {/* ── Wholesale Price ── */}
          <label className="addinv-name-label">
            Wholesale Price{" "}
            <small>(optional — price charged to bulk/wholesale buyers)</small>
          </label>
          <div className="addinv-price-input-wrapper" ref={fieldRefs.wholesale_price}>
            <span>₦</span>
            <input
              type="number"
              placeholder="Wholesale Price (optional)"
              className={errors.wholesale_price ? "addinv-input-error" : ""}
              value={manualDrug.wholesale_price}
              min="0"
              step="0.01"
              onChange={(e) =>
                setManualDrug({ ...manualDrug, wholesale_price: e.target.value })
              }
            />
          </div>
          {errors.wholesale_price && (
            <p className="addinv-error-message">{errors.wholesale_price}</p>
          )}

          {/* ── Cost Price ── */}
          <label className="addinv-name-label">
            Cost Price{" "}
            <small>(optional — used to calculate profit in POS analytics)</small>
          </label>
          <div className="addinv-price-input-wrapper" ref={fieldRefs.cost_price}>
            <span>₦</span>
            <input
              type="number"
              placeholder="Cost Price (optional)"
              className={errors.cost_price ? "addinv-input-error" : ""}
              value={manualDrug.cost_price}
              min="0"
              step="0.01"
              onChange={(e) =>
                setManualDrug({ ...manualDrug, cost_price: e.target.value })
              }
            />
          </div>
          {errors.cost_price && (
            <p className="addinv-error-message">{errors.cost_price}</p>
          )}

          {/* ── Live margin preview ── */}
          {manualDrug.price !== "" &&
            manualDrug.cost_price !== "" &&
            parseFloat(manualDrug.price) > 0 &&
            parseFloat(manualDrug.cost_price) >= 0 && (
              <div className="addinv-margin-preview">
                {(() => {
                  const selling = parseFloat(manualDrug.price);
                  const cost = parseFloat(manualDrug.cost_price);
                  const profit = selling - cost;
                  const margin =
                    selling > 0 ? ((profit / selling) * 100).toFixed(1) : 0;
                  const isPositive = profit >= 0;
                  return (
                    <p
                      style={{
                        color: isPositive ? "#2e7d32" : "#c62828",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        margin: "4px 0 8px",
                      }}
                    >
                      {isPositive ? "📈" : "📉"} Retail profit per unit: ₦
                      {profit.toFixed(2)} ({margin}% margin)
                    </p>
                  );
                })()}
              </div>
            )}

          {/* ── Wholesale margin preview ── */}
          {manualDrug.wholesale_price !== "" &&
            manualDrug.cost_price !== "" &&
            parseFloat(manualDrug.wholesale_price) > 0 &&
            parseFloat(manualDrug.cost_price) >= 0 && (
              <div className="addinv-margin-preview">
                {(() => {
                  const wholesale = parseFloat(manualDrug.wholesale_price);
                  const cost = parseFloat(manualDrug.cost_price);
                  const profit = wholesale - cost;
                  const margin =
                    wholesale > 0 ? ((profit / wholesale) * 100).toFixed(1) : 0;
                  const isPositive = profit >= 0;
                  return (
                    <p
                      style={{
                        color: isPositive ? "#1565c0" : "#c62828",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        margin: "4px 0 8px",
                      }}
                    >
                      {isPositive ? "📦" : "📉"} Wholesale profit per unit: ₦
                      {profit.toFixed(2)} ({margin}% margin)
                    </p>
                  );
                })()}
              </div>
            )}

          {/* ── Expiration Date ── */}
          <div className="addinv-form-group">
            <label className="addinv-name-label">
              Expiration Date <small>(optional)</small>
            </label>
            <input
              type="date"
              min={new Date().toISOString().split("T")[0]}
              value={manualDrug.expiration_date}
              onChange={(e) =>
                setManualDrug({
                  ...manualDrug,
                  expiration_date: e.target.value,
                })
              }
            />
          </div>

          {/* ── NAFDAC ── */}
          <input
            type="text"
            placeholder="NAFDAC Number (optional)"
            value={manualDrug.nafdac_number}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, nafdac_number: e.target.value })
            }
          />

          {/* ── Strength ── */}
          <input
            type="text"
            placeholder="Strength (optional)"
            value={manualDrug.strength}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, strength: e.target.value })
            }
          />

          {/* ── Dosage Form ── */}
          <select
            value={manualDrug.dosage_form}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, dosage_form: e.target.value })
            }
          >
            <option value="">Select Dosage Form (optional)</option>
            {[
              "Tablet","Capsule","Syrup","Suspension","Injection",
              "Cream","Ointment","Gel","Lotion","Inhaler",
              "Drops","Suppository","Patch",
            ].map((form) => (
              <option key={form} value={form}>{form}</option>
            ))}
          </select>

          {/* ── Optional fields ── */}
          <input
            type="text"
            placeholder="Batch Number (optional)"
            value={manualDrug.batch_number}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, batch_number: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Pack Size (optional)"
            value={manualDrug.pack_size}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, pack_size: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Unit (optional)"
            value={manualDrug.unit}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, unit: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Manufacturer (optional)"
            value={manualDrug.manufacturer}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, manufacturer: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={manualDrug.description}
            onChange={(e) =>
              setManualDrug({ ...manualDrug, description: e.target.value })
            }
          />

          <button type="submit" disabled={loading}>
            {loading ? "Adding..." : "➕ Add Item"}
          </button>

          <div ref={bottomRef}>
            {errors.form && (
              <div className="addinv-error-message" style={{ marginTop: "1rem" }}>
                {errors.form}
              </div>
            )}
            {success && (
              <div className="addinv-success-message" style={{ marginTop: "1rem" }}>
                {success}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInventoryManually;